import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { firstVoucherImagePath } from '../utils/vioAssetUrl';
import { htmlToPlainText } from '../utils/htmlToPlainText';

function toFiniteNumber(value) {
  if (value == null) return NaN;
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function localizedText(value) {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  return firstNonEmptyString(value.en, value.zh, value.tc, value.sc);
}

function voucherSelectionKey(voucher) {
  const campaignKey = voucher?.campaignId || 'no-campaign';
  const voucherKey =
    voucher?.campaignVoucherSchemaId ||
    voucher?.voucherId ||
    voucher?._id ||
    'unknown-voucher';
  return `${campaignKey}:${voucherKey}`;
}

function normalizeVoucher(item) {
  const schema = item?.voucherSchema || {};
  const source = Object.keys(schema).length ? schema : item || {};
  const campaignQuantity =
    item?.campaignQuantity ??
    source.campaignQuantity ??
    source.quantity ??
    item?.quantity;
  const campaignRemainingQuantity =
    item?.campaignRemainingQuantity ??
    source.campaignRemainingQuantity ??
    item?.remainingQuantity ??
    source.remainingQuantity;
  const campaignClaimedQuantity =
    item?.campaignClaimedQuantity ??
    source.campaignClaimedQuantity ??
    item?.claimedQuantity ??
    source.claimedQuantity;
  const isLimited = source.isLimited ?? source.hasLimitedQuantity ?? campaignQuantity != null;
  const campaignVoucherSchemaId =
    item?.campaignVoucherSchemaId ||
    source.campaignVoucherSchemaId ||
    source.voucherSchemaId ||
    item?._id ||
    null;
  const voucherId =
    source.voucherId ||
    source.voucherSchemaId ||
    item?.voucherId ||
    item?.campaignVoucherSchemaId ||
    item?._id ||
    null;
  const totalQuantity =
    campaignQuantity ??
    source.totalQuantity ??
    item?.totalQuantity ??
    (isLimited ? 0 : -1);
  const claimedQuantity =
    campaignClaimedQuantity ??
    (item?.isMaxQuantity ? totalQuantity : 0);
  const remainingQuantity =
    campaignRemainingQuantity ??
    (isLimited
      ? Math.max(
        0,
        (toFiniteNumber(totalQuantity) || 0) - (toFiniteNumber(claimedQuantity) || 0)
      )
      : undefined);

  const imageCandidates = [
    source.images,
    item?.images,
    source.image?.imageUrl ? [source.image.imageUrl] : null,
    typeof source.image === 'string' ? [source.image] : null,
    typeof item?.image === 'string' ? [item.image] : null,
    typeof source.coverImage === 'string' ? [source.coverImage] : null,
    typeof source.thumbnail === 'string' ? [source.thumbnail] : null,
    typeof source.thumbnailUrl === 'string' ? [source.thumbnailUrl] : null,
    typeof item?.thumbnail === 'string' ? [item.thumbnail] : null,
    typeof item?.thumbnailUrl === 'string' ? [item.thumbnailUrl] : null,
  ];
  const images = imageCandidates.find((candidate) => Array.isArray(candidate) && candidate.length) || [];

  const catRaw = source.category ?? item?.category ?? source.categoryName ?? item?.categoryName;
  let categoryLabel = '';
  if (Array.isArray(source.categories) && source.categories.length > 0) {
    const first = source.categories[0];
    categoryLabel =
      typeof first === 'string'
        ? first.trim()
        : localizedText(first) || '';
  }
  if (!categoryLabel) {
    categoryLabel =
      localizedText(catRaw) || (typeof catRaw === 'string' ? catRaw.trim() : '') || '';
  }

  return {
    _id: voucherId || campaignVoucherSchemaId,
    voucherId,
    campaignVoucherSchemaId,
    /** Present when VIO published list includes per-campaign stock (see miniappService.getPublishedVoucherSchemas). */
    campaignId: item?.campaignId || source.campaignId || '',
    selectionKey: voucherSelectionKey({
      campaignId: item?.campaignId || source.campaignId || '',
      campaignVoucherSchemaId,
      voucherId,
    }),
    name: localizedText(source.name) || localizedText(source.voucherSchemaName) || firstNonEmptyString(source.title, item?.name),
    description: localizedText(source.description) || localizedText(item?.description) || '',
    totalQuantity,
    claimedQuantity,
    remainingQuantity,
    maxClaimsPerUser:
      source.maxClaimsPerUser ??
      source.maxPurchasePerUser ??
      item?.maxClaimsPerUser ??
      item?.maxPurchasePerUser ??
      null,
    images,
    value: source.value ?? item?.value ?? 0,
    valueCurrency: source.valueCurrency || item?.valueCurrency || '',
    valueType: source.valueType || item?.valueType || '',
    voucherType: source.accessType || source.voucherType || item?.voucherType || '',
    categoryLabel: categoryLabel.trim(),
  };
}

/**
 * VIO: totalQuantity < 0 means unlimited. remainingQuantity -1 = unlimited in campaign context.
 */
function voucherIsUnlimitedStock(v) {
  const rem = toFiniteNumber(v.remainingQuantity);
  if (Number.isFinite(rem) && rem < 0) return true;

  const raw = v.totalQuantity;
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) return true;

  const total = toFiniteNumber(typeof raw === 'string' ? raw.trim() : raw);
  if (!Number.isFinite(total)) return true;
  return total < 0;
}

/** luckydraw SetLuckyDraw: campaign / global stock line */
function isVioVoucherGlobalUnlimited(v) {
  const rem = Number(v?.remainingQuantity);
  if (Number.isFinite(rem) && rem < 0) return true;
  const t = Number(v?.totalQuantity);
  return !Number.isFinite(t) || t <= 0;
}

function getVioVoucherGlobalStockUi(v) {
  const claimed = Number(v?.claimedQuantity) || 0;
  if (isVioVoucherGlobalUnlimited(v)) {
    return {
      unlimited: true,
      outOfGlobalStock: false,
      claimed,
    };
  }
  const directRemaining = Number(v?.remainingQuantity);
  if (Number.isFinite(directRemaining) && directRemaining >= 0) {
    const total = Number(v?.totalQuantity);
    return {
      unlimited: false,
      outOfGlobalStock: directRemaining <= 0,
      remaining: directRemaining,
      total: Number.isFinite(total) && total > 0 ? total : directRemaining + claimed,
      claimed,
    };
  }
  const total = Number(v.totalQuantity);
  const remaining = Math.max(0, total - claimed);
  return {
    unlimited: false,
    outOfGlobalStock: remaining <= 0,
    remaining,
    total,
    claimed,
  };
}

function formatVioMaxClaimsPerUserLabel(maxClaimsPerUser) {
  if (maxClaimsPerUser == null || maxClaimsPerUser === '') return 'Max per user: 1';
  const m = Number(maxClaimsPerUser);
  if (!Number.isFinite(m)) return `Max per user: ${maxClaimsPerUser}`;
  if (m <= 0) return 'Max per user: Unlimited';
  return `Max per user: ${m}`;
}

function hasVoucherPerUserClaimLimit(v) {
  const raw = v?.maxClaimsPerUser;
  if (raw == null || raw === '') return true;
  const n = Number(raw);
  return !Number.isFinite(n) || n > 0;
}

function isVoucherSelectable(voucher) {
  if (hasVoucherPerUserClaimLimit(voucher)) return false;

  if (voucherIsUnlimitedStock(voucher)) return true;

  const total = toFiniteNumber(voucher.totalQuantity);
  const cap = Number.isFinite(total) && total >= 0 ? total : 0;
  const claimed = toFiniteNumber(voucher.claimedQuantity) || 0;

  if (voucher.remainingQuantity != null && voucher.remainingQuantity !== '') {
    const r = toFiniteNumber(voucher.remainingQuantity);
    if (Number.isFinite(r) && r >= 0) return r > 0;
  }

  return Math.max(0, cap - claimed) > 0;
}

function VoucherModal({ isOpen, onClose, onApply, initialVoucher }) {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [search, setSearch] = useState('');

  const handleClose = useCallback(() => {
    setSearch('');
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    setSelectedKey(initialVoucher ? voucherSelectionKey(initialVoucher) : null);
    setSearch('');
    fetchVouchers();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/vio/vouchers', { params: { page: 1, size: 200 } });
      const items = res.data?.data || res.data?.message?.data || [];
      const mapped = items
        .map(normalizeVoucher)
        .filter((item) => item._id && item.name);
      setVouchers(mapped);
    } catch (err) {
      console.error('Failed to fetch vouchers:', err);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || loading || !selectedKey) return;
    const v = vouchers.find((x) => x.selectionKey === selectedKey);
    if (!v || !isVoucherSelectable(v)) setSelectedKey(null);
  }, [isOpen, loading, vouchers, selectedKey]);

  const handleConfirm = () => {
    const voucher = vouchers.find((v) => v.selectionKey === selectedKey);
    if (!voucher || !isVoucherSelectable(voucher)) return;

    onApply({
      type: 'voucher',
      voucherId: voucher.voucherId || voucher._id,
      campaignVoucherSchemaId: voucher.campaignVoucherSchemaId || '',
      campaignId: voucher.campaignId || '',
      voucherName: voucher.name,
      voucherImage: firstVoucherImagePath(voucher),
      voucherValue: Number(voucher.value) || 0,
      voucherValueCurrency: (voucher.valueCurrency || '').trim(),
      voucherValueType: (voucher.valueType || '').trim(),
      voucherType: (voucher.voucherType || '').trim(),
      voucherCategory: (voucher.categoryLabel || '').trim(),
      tokenId: '',
      tokenName: '',
      tokenSymbol: '',
      tokenAmount: 0,
      tokenIcon: '',
    });
    handleClose();
  };

  const filtered = vouchers.filter((v) => {
    const q = search.toLowerCase();
    if (!q) return true;
    const plainDesc = htmlToPlainText(v.description).toLowerCase();
    return (
      v.name?.toLowerCase().includes(q) ||
      plainDesc.includes(q)
    );
  });

  if (!isOpen) return null;

  const selectedVoucher = selectedKey
    ? vouchers.find((x) => x.selectionKey === selectedKey)
    : null;
  const canConfirm = !!(selectedVoucher && isVoucherSelectable(selectedVoucher));

  return createPortal(
    <div className="modal-overlay modal-overlay--nested">
      <div className="modal modal--voucher-picker" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Voucher</h3>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
          >
            &times;
          </button>
        </div>
        <div className="modal-body modal-body--voucher-picker">
          <div className="voucher-picker-search">
            <label htmlFor="voucher-picker-search-input" className="sr-only">Search vouchers by name</label>
            <div className="voucher-picker-search-inner">
              <span className="voucher-picker-search-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                id="voucher-picker-search-input"
                type="search"
                className="voucher-picker-search-input"
                placeholder="Search by voucher name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          {loading ? (
            <div className="voucher-picker-loading">
              <div className="spinner" />
              <span>Loading vouchers…</span>
            </div>
          ) : (
            <div className="coupon-list coupon-list--picker">
              {filtered.length === 0 ? (
                <p className="voucher-picker-empty">No vouchers match your search.</p>
              ) : (
                filtered.map((v) => {
                  const stockUi = getVioVoucherGlobalStockUi(v);
                  const perUserLimited = hasVoucherPerUserClaimLimit(v);
                  const disabled = !isVoucherSelectable(v);
                  const isPrivate =
                    typeof v.voucherType === 'string' &&
                    v.voucherType.toLowerCase().includes('private');

                  return (
                    <div
                      key={v.selectionKey}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      className={`coupon-card${selectedKey === v.selectionKey ? ' selected' : ''}${disabled ? ' row-disabled' : ''}`}
                      onClick={() => {
                        if (disabled) return;
                        setSelectedKey(v.selectionKey);
                      }}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedKey(v.selectionKey);
                        }
                      }}
                    >
                      <div className="coupon-info">
                        <div className="coupon-name-row">
                          <strong>{v.name}</strong>
                          {v.voucherType ? (
                            <span className={`voucher-chip voucher-chip--${isPrivate ? 'private' : 'public'}`}>
                              {isPrivate ? 'Private' : 'Public'}
                            </span>
                          ) : null}
                        </div>
                        <span className="coupon-meta">
                          {v.campaignId
                            ? stockUi.unlimited
                              ? `Campaign: Unlimited (${stockUi.claimed} issued)`
                              : `Campaign: ${stockUi.remaining}/${stockUi.total}`
                            : stockUi.unlimited
                              ? `Qty: Unlimited (${stockUi.claimed} issued)`
                              : `Qty: ${stockUi.remaining}/${stockUi.total}`}
                          {' · '}
                          {formatVioMaxClaimsPerUserLabel(v.maxClaimsPerUser)}
                        </span>
                        {disabled && (
                          <span className="coupon-meta coupon-meta--error">
                            {perUserLimited
                              ? 'Has per-user claim limit — not suitable for rewards'
                              : 'Out of stock'}
                          </span>
                        )}
                      </div>
                      <div className="coupon-right">
                        {v.value > 0 && (
                          <span className="coupon-value-badge">
                            {v.valueType === 'percentage'
                              ? `${v.value}%`
                              : `${v.value}${v.valueCurrency ? ` ${v.valueCurrency}` : ''}`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        <div className="modal-footer modal-footer--voucher-picker">
          <button
            type="button"
            className="btn btn-primary voucher-picker-confirm"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VoucherModal;
